import type {
  AddressSuggestion,
  DestinationSuggestion,
  GeocodingProviderId,
} from '@/interfaces/geocoding';

/**
 * Geocoding for destination search and residence addresses.
 *
 * LocationIQ is tried first: it is faster and handles half-typed addresses
 * ("Hauptstr 190 Heidel") better. Geoapify answers when LocationIQ fails,
 * is rate limited, or finds nothing. A provider without a key is skipped.
 */

export const GERMAN_STATES = [
  'Baden-Württemberg',
  'Bayern',
  'Berlin',
  'Brandenburg',
  'Bremen',
  'Hamburg',
  'Hessen',
  'Mecklenburg-Vorpommern',
  'Niedersachsen',
  'Nordrhein-Westfalen',
  'Rheinland-Pfalz',
  'Saarland',
  'Sachsen',
  'Sachsen-Anhalt',
  'Schleswig-Holstein',
  'Thüringen',
] as const;

const LIMIT = '5';

const foldName = (value: string): string =>
  value.trim().toLowerCase().replaceAll('ß', 'ss').normalize('NFD').replace(/\p{M}/gu, '');

const germanStateNamed = (name: string | undefined): string | undefined => {
  if (!name) {
    return undefined;
  }
  const folded = foldName(name);
  return GERMAN_STATES.find((state) => foldName(state) === folded);
};

/**
 * Providers sometimes use the official name ("Freie Hansestadt Bremen", "Freistaat Bayern").
 * Residences and search use the short name, so fold those onto it.
 */
const shortStateName = (state: string | undefined): string | undefined => {
  if (!state) {
    return undefined;
  }
  const folded = foldName(state);
  const match = GERMAN_STATES.find((candidate) => {
    const short = foldName(candidate);
    return folded === short || folded.endsWith(` ${short}`);
  });
  return match ?? state;
};

/** States are a fixed list, so they are matched locally instead of asking a provider. */
export const matchGermanStates = (query: string): DestinationSuggestion[] => {
  const folded = foldName(query);
  if (folded.length < 2) {
    return [];
  }
  return GERMAN_STATES.filter((state) => foldName(state).startsWith(folded)).map((state) => ({
    id: `state:${state}`,
    kind: 'state',
    name: state,
    state,
    label: state,
  }));
};

const isGermany = (countryCode: string | undefined): boolean =>
  !countryCode || countryCode.toLowerCase() === 'de';

const uniqueBy = <T>(items: T[], key: (item: T) => string): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const value = key(item);
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

const cityLabel = (name: string, state: string): string =>
  state && state !== name ? `${name}, ${state}` : name;

const addressLabel = (address: Omit<AddressSuggestion, 'id' | 'label'>): string => {
  const street = [address.street, address.houseNumber].filter(Boolean).join(' ');
  const place = [address.postcode, address.city].filter(Boolean).join(' ');
  return [street, place].filter(Boolean).join(', ');
};

type LocationIqAddress = {
  name?: string;
  house_number?: string;
  road?: string;
  suburb?: string;
  city?: string;
  town?: string;
  village?: string;
  county?: string;
  state?: string;
  postcode?: string;
  country_code?: string;
};

type LocationIqResult = {
  place_id?: string;
  lat?: string;
  lon?: string;
  class?: string;
  type?: string;
  address?: LocationIqAddress;
};

const locationIqResults = (payload: unknown): LocationIqResult[] =>
  Array.isArray(payload) ? (payload as LocationIqResult[]) : [];

export const destinationsFromLocationIq = (payload: unknown): DestinationSuggestion[] => {
  const hits: DestinationSuggestion[] = [];
  for (const result of locationIqResults(payload)) {
    const address = result.address;
    const name = address?.name;
    if (!address || !name || !isGermany(address.country_code)) {
      continue;
    }
    // District boundaries ("Frankfurt (Oder) Nord") carry their parent city.
    if (address.suburb || (result.class === 'boundary' && address.city && address.city !== name)) {
      continue;
    }
    // Berlin and Hamburg are the state themselves, so the address has no state.
    const state = shortStateName(address.state) ?? germanStateNamed(name) ?? '';
    hits.push({
      id: `locationiq:${result.place_id ?? `${name}|${state}`}`,
      kind: 'city',
      name,
      state,
      label: cityLabel(name, state),
    });
  }
  return uniqueBy(hits, (hit) => `${hit.name}|${hit.state}`);
};

export const addressesFromLocationIq = (payload: unknown): AddressSuggestion[] => {
  const hits: AddressSuggestion[] = [];
  for (const result of locationIqResults(payload)) {
    const address = result.address;
    const lat = Number(result.lat);
    const lon = Number(result.lon);
    // A street or square result has no `road`; its name is the street.
    const isStreet =
      result.class === 'highway' || (result.class === 'place' && result.type === 'square');
    const street = address?.road ?? (isStreet ? address?.name : undefined);
    if (!address || !street || !isGermany(address.country_code)) {
      continue;
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      continue;
    }
    const city = address.city ?? address.town ?? address.village ?? address.county ?? '';
    const fields = {
      street,
      houseNumber: address.house_number ?? '',
      postcode: address.postcode ?? '',
      city,
      state: shortStateName(address.state) ?? germanStateNamed(city) ?? '',
      lat,
      lon,
    };
    hits.push({ id: `locationiq:${result.place_id}`, label: addressLabel(fields), ...fields });
  }
  // Shops and museums at the same door repeat the address; keep one row per address.
  return uniqueBy(hits, (hit) => hit.label);
};

type GeoapifyResult = {
  place_id?: string;
  result_type?: string;
  name?: string;
  street?: string;
  housenumber?: string;
  postcode?: string;
  city?: string;
  state?: string;
  country_code?: string;
  lat?: number;
  lon?: number;
};

const geoapifyResults = (payload: unknown): GeoapifyResult[] =>
  (payload as { results?: GeoapifyResult[] } | null)?.results ?? [];

export const destinationsFromGeoapify = (payload: unknown): DestinationSuggestion[] => {
  const hits: DestinationSuggestion[] = [];
  for (const result of geoapifyResults(payload)) {
    if (result.result_type !== 'city' || !isGermany(result.country_code)) {
      continue;
    }
    // Districts come back as `city` results named after the district.
    if (result.name && result.city && result.name !== result.city) {
      continue;
    }
    const name = result.city ?? result.name;
    if (!name) {
      continue;
    }
    const state = shortStateName(result.state) ?? germanStateNamed(name) ?? '';
    hits.push({
      id: `geoapify:${result.place_id ?? `${name}|${state}`}`,
      kind: 'city',
      name,
      state,
      label: cityLabel(name, state),
    });
  }
  return uniqueBy(hits, (hit) => `${hit.name}|${hit.state}`);
};

export const addressesFromGeoapify = (payload: unknown): AddressSuggestion[] => {
  const hits: AddressSuggestion[] = [];
  for (const result of geoapifyResults(payload)) {
    if (!result.street || !isGermany(result.country_code)) {
      continue;
    }
    if (result.lat == null || result.lon == null) {
      continue;
    }
    const city = result.city ?? '';
    const fields = {
      street: result.street,
      houseNumber: result.housenumber ?? '',
      postcode: result.postcode ?? '',
      city,
      state: shortStateName(result.state) ?? germanStateNamed(city) ?? '',
      lat: result.lat,
      lon: result.lon,
    };
    hits.push({ id: `geoapify:${result.place_id}`, label: addressLabel(fields), ...fields });
  }
  return uniqueBy(hits, (hit) => hit.label);
};

type ProviderRequest<T> = {
  provider: GeocodingProviderId;
  url: () => URL | null;
  parse: (payload: unknown) => T[];
};

const fetchProvider = async <T>(request: ProviderRequest<T>): Promise<T[]> => {
  const url = request.url();
  if (!url) {
    return [];
  }
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  // LocationIQ answers 404 when nothing matches.
  if (response.status === 404) {
    return [];
  }
  if (!response.ok) {
    throw new Error(`${request.provider} geocoding failed with ${response.status}`);
  }
  return request.parse(await response.json());
};

/** First provider that answers with results wins. */
const firstWithResults = async <T>(requests: ProviderRequest<T>[]): Promise<T[]> => {
  for (const request of requests) {
    try {
      const hits = await fetchProvider(request);
      if (hits.length) {
        return hits;
      }
    } catch {
      // Try the next provider.
    }
  }
  return [];
};

const locationIqUrl = (path: string, params: Record<string, string>): URL | null => {
  const key = import.meta.env.VITE_LOCATIONIQ_API_KEY;
  if (!key) {
    return null;
  }
  const url = new URL(`https://api.locationiq.com/v1/${path}`);
  url.search = new URLSearchParams({
    key,
    countrycodes: 'de',
    'accept-language': 'de',
    limit: LIMIT,
    dedupe: '1',
    ...params,
  }).toString();
  return url;
};

const geoapifyUrl = (params: Record<string, string>): URL | null => {
  const key = import.meta.env.VITE_GEOAPIFY_API_KEY;
  if (!key) {
    return null;
  }
  const url = new URL('https://api.geoapify.com/v1/geocode/autocomplete');
  url.search = new URLSearchParams({
    apiKey: key,
    filter: 'countrycode:de',
    lang: 'de',
    limit: LIMIT,
    format: 'json',
    ...params,
  }).toString();
  return url;
};

export const searchDestinations = async (query: string): Promise<DestinationSuggestion[]> => {
  const trimmed = query.trim();
  if (trimmed.length < 2) {
    return [];
  }
  const cities = await firstWithResults<DestinationSuggestion>([
    {
      provider: 'locationiq',
      url: () =>
        locationIqUrl('autocomplete', {
          q: trimmed,
          tag: 'place:city,place:town,place:village,boundary:administrative',
        }),
      parse: destinationsFromLocationIq,
    },
    {
      provider: 'geoapify',
      url: () => geoapifyUrl({ text: trimmed, type: 'city' }),
      parse: destinationsFromGeoapify,
    },
  ]);
  // Berlin, Hamburg and Bremen are both a city and a state; keep the city row only.
  const states = matchGermanStates(trimmed).filter(
    (state) => !cities.some((city) => city.name === state.name)
  );
  return [...cities, ...states];
};

/** "Hauptstr 190 Heidel" -> "190". Five-digit runs are postcodes, not house numbers. */
export const houseNumberInQuery = (query: string): string | undefined =>
  query
    .match(/(?:^|\s)(\d{1,4}\s?[a-z]?)(?=[\s,]|$)/i)?.[1]
    ?.replace(/\s/g, '')
    .toLowerCase();

/** "16-20" covers 16; "9a" only matches "9a". */
const coversHouseNumber = (houseNumber: string, wanted: string): boolean => {
  const normalized = houseNumber.replace(/\s/g, '').toLowerCase();
  if (normalized === wanted) {
    return true;
  }
  const range = normalized.match(/^(\d+)-(\d+)$/);
  const number = Number(wanted);
  return Boolean(range && number >= Number(range[1]) && number <= Number(range[2]));
};

/** Exact house-number matches first, the provider's order otherwise. */
export const rankByHouseNumber = (
  hits: AddressSuggestion[],
  wanted: string | undefined
): AddressSuggestion[] => {
  if (!wanted) {
    return hits;
  }
  const exact = hits.filter((hit) => coversHouseNumber(hit.houseNumber, wanted));
  return [...exact, ...hits.filter((hit) => !exact.includes(hit))];
};

const addressRequests = (query: string): ProviderRequest<AddressSuggestion>[] => [
  {
    provider: 'locationiq',
    url: () => locationIqUrl('autocomplete', { q: query }),
    parse: addressesFromLocationIq,
  },
  {
    provider: 'geoapify',
    url: () => geoapifyUrl({ text: query }),
    parse: addressesFromGeoapify,
  },
];

export const searchAddresses = async (query: string): Promise<AddressSuggestion[]> => {
  const trimmed = query.trim();
  if (trimmed.length < 3) {
    return [];
  }
  const wanted = houseNumberInQuery(trimmed);
  const [primary, fallback] = addressRequests(trimmed);
  const hits = rankByHouseNumber(await firstWithResults([primary, fallback]), wanted);
  if (!wanted || hits.some((hit) => coversHouseNumber(hit.houseNumber, wanted))) {
    return hits;
  }
  // LocationIQ misses some house numbers that Geoapify has ("Alter Markt 9, Potsdam").
  const more = await fetchProvider(fallback).catch(() => []);
  return uniqueBy(rankByHouseNumber([...more, ...hits], wanted), (hit) => hit.label);
};
